﻿import React, { forwardRef, useEffect } from "react";
import { Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  setHoveredWorkflow,
  selectHoveredWorkflow,
} from "@/features/homepage/homeSlice";
import { setSelectedWorkflow } from "@/features/homepage/workflowManagerSessionSlice";
import {
  CreditNoteIcon,
  CustomerOrderAcknowledgmentIcon,
  CustomerCreditNoteIcon,
  CustomerInvoiceIcon,
  CustomerOrderIcon,
  CustomerOrderChangeIcon,
  CustomerOrderChangeAcknowledgmentIcon,
  CustomerOrderStatusIcon,
  CustomerPrepaymentIcon,
  InvoiceIcon,
  PackedOrderIcon,
  PickOrderIcon,
  RoutingRequestIcon,
  RoutingRequestConfirmationIcon,
  SalesOrderIcon,
  ShipmentIcon,
  ShipmentNoticeIcon,
} from "@/ImageRepo";
import { InsertPhoto as InsertPhotoIcon} from "@mui/icons-material";
import "./ArtifactBlock.css";

const getIconByName = (name) => {
  switch (name) {
    case "Customer Order":
      return <CustomerOrderIcon />;
    case "Customer Order Ack":
      return <CustomerOrderAcknowledgmentIcon />;
    case "Customer Order Change":
      return <CustomerOrderChangeIcon />;
    case "Customer Order Change Ack":
      return <CustomerOrderChangeAcknowledgmentIcon />;
    case "Customer Order Status":
      return <CustomerOrderStatusIcon />;
    case "Pick Order":
      return <PickOrderIcon />;
    case "Packed Order":
      return <PackedOrderIcon />;
    case "Customer Routing Request":
      return <RoutingRequestIcon />;
    case "Customer Routing Confirmation":
      return <RoutingRequestConfirmationIcon />;
    case "Customer Shipment Notice":
      return <ShipmentNoticeIcon />;
    case "Sales Order":
      return <SalesOrderIcon />;
    case "Customer Invoice":
      return <CustomerInvoiceIcon />;
    case "Invoice":
      return <InvoiceIcon />;
    case "Credit Note":
      return <CreditNoteIcon />;
    case "Customer Credit Note":
      return <CustomerCreditNoteIcon />;
    case "Shipment":
      return <ShipmentIcon />;
    case "Customer Prepayment":
      return <CustomerPrepaymentIcon/>;
    default:
      return <InsertPhotoIcon />;
  }
};

const ArtifactBlock = forwardRef(
  (
    {
      name,
      className,
      connectionCount,
      x,
      y,
      alignment = "right",
      artifactState,
      setArtifactState,
      selectedWorkflow,
      associatedWorkflowOperationIds,
    },
    ref
  ) => {
    const dispatch = useDispatch();
    const hoveredWorkflow = useSelector(selectHoveredWorkflow);

    useEffect(() => {
      // Check if any workflow in selectedWorkflows matches associatedWorkflowOperationIds
      const isSelected = selectedWorkflow.some((workflow) =>
        associatedWorkflowOperationIds.includes(workflow)
      );
      if (isSelected) {
        setArtifactState("selected");
      } else if (selectedWorkflow.includes(0)) {
        setArtifactState("default");
      } else {
        setArtifactState("lowlighted");
      }
    }, [selectedWorkflow, associatedWorkflowOperationIds, setArtifactState]);

    const handleMouseEnter = () => {
      if (artifactState !== "selected" && artifactState !== "lowlighted") {
        setArtifactState("hovered");
        dispatch(
          setHoveredWorkflow({
            hoveredWorkflow: associatedWorkflowOperationIds[0],
          })
        );
      }
    };

    const handleMouseLeave = () => {
      if (artifactState !== "selected" && artifactState !== "lowlighted") {
        setArtifactState("default");
        dispatch(setHoveredWorkflow({ hoveredWorkflow: 0 }));
      }
    };

    const handleClick = () => {
      if (selectedWorkflow === associatedWorkflowOperationIds[0]) {
        dispatch(setSelectedWorkflow({ selectedWorkflow: 0 }));
      } else {
        dispatch(
          setSelectedWorkflow({
            selectedWorkflow: associatedWorkflowOperationIds[0],
          })
        );

        // heap.io
        window.heap.track("Workflow Map - Select Workflow");
      }
    };

    const containerClassName = `artifact-block-container ${
      associatedWorkflowOperationIds.includes(hoveredWorkflow)
        ? "workflow-hovered"
        : ""
    }`;

    return (
      <div
        className={containerClassName}
        style={{ left: x, top: y }}
        title={name}
      >
        <div
          ref={ref}
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className={`artifact-block ${artifactState} ${containerClassName}`}
        >
          {connectionCount !== 0 && (
            <div
              className={`artifact-block-badge ${alignment} ${artifactState}`}
            >
              <Typography
                variant="h2"
                style={{
                  fontSize: "var(--font-md)",
                }}
              >
                {connectionCount}
              </Typography>
            </div>
          )}
          <div className="artifact-block-content">
            {getIconByName(name)}
            <Typography variant="body2">{name}</Typography>
          </div>
        </div>
      </div>
    );
  }
);

export default ArtifactBlock;
